import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';

import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import GiftLoader from '../../common/GiftLoader';

// Extracted components
import FAQSection from './FAQSection';
import TicketTable from './TicketTable';
import SubmitTicketModal from './SubmitTicketModal';
import TicketDetailsView from './TicketDetailsView';

// Custom hooks
import { useHelpSupportData } from './hooks/useHelpSupportData';

import './HelpSupport.scss';

interface Issue {
  id?: string;
  _id?: string;
  ticketId?: string;
  subject?: string;
  title?: string;
  description: string;
  status: 'Open' | 'In Progress' | 'Resolved';
  submittedOn?: string;
  createdAt?: string;
  createdDate?: string;
  replies?: any[];
  conversation?: Array<{
    _id?: string;
    sender: string;
    message: string;
    timestamp: string;
    attachments?: string[];
  }>;
}

interface IssueForm {
  subject: string;
  description: string;
  attachments?: FileList | null | undefined;
}

const HelpSupport: React.FC = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<FileList | null>(null);

  const navigate = useNavigate();
  const { ticketId } = useParams();

  // Custom hook for data management
  const {
    issues,
    faqs,
    loading,
    ticketsLoading,
    ticketDetailLoading,
    fetchTicketDetails,
    submitTicket,
    submitReply
  } = useHelpSupportData();

  // Handle URL-based ticket selection
  useEffect(() => {
    const loadTicketFromUrl = async () => {
      if (ticketId && !selectedIssue) {
        try {
          const ticketDetails = await fetchTicketDetails(ticketId);
          if (ticketDetails) {
            setSelectedIssue(ticketDetails);
          } else {
            // If ticket not found, redirect to main help support page
            navigate('/help-support', { replace: true });
          }
        } catch (error) {
          console.error('Error loading ticket from URL:', error);
          navigate('/help-support', { replace: true });
        }
      } else if (!ticketId && selectedIssue) {
        // If no ticketId in URL but we have a selected issue, clear it
        setSelectedIssue(null);
      }
    };

    loadTicketFromUrl();
  }, [ticketId, selectedIssue, fetchTicketDetails, navigate]);

  // Ticket submission handler
  const handleSubmitIssue = async (values: IssueForm, { resetForm, setSubmitting }: any) => {
    setSubmitting(true);
    try {
      const success = await submitTicket(values);
      if (success) {
        resetForm();
        setShowSubmitModal(false);
      }
    } catch (error) {
      console.error('Error in handleSubmitIssue:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Ticket details fetch handler
  const handleTicketClick = (clickedTicketId: string) => {
    // Navigate to ticket detail URL instead of directly setting state
    navigate(`/help-support/ticket/${clickedTicketId}`);
  };

  // Reply submission handler
  const handleReplySubmit = async () => {
    if (!replyText.trim() || !selectedIssue) return;

    const ticketId = selectedIssue.id || selectedIssue._id;
    if (!ticketId) {
      console.error('No ticket ID found');
      return;
    }

    try {
      const success = await submitReply(ticketId, replyText, replyFiles);
      if (success) {
        setReplyText('');
        setReplyFiles(null);

        // Refresh ticket details
        const updatedTicket = await fetchTicketDetails(ticketId);
        if (updatedTicket) {
          setSelectedIssue(updatedTicket);
        }
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  // Show ticket details view if a ticket is selected
  if (selectedIssue) {
    return (
      <TicketDetailsView
        selectedIssue={selectedIssue}
        replyText={replyText}
        replyFiles={replyFiles}
        onBack={() => navigate('/help-support')}
        onReplyTextChange={setReplyText}
        onReplyFilesChange={setReplyFiles}
        onReplySubmit={handleReplySubmit}
      />
    );
  }

  // Show ticket detail loading when loading ticket details
  if (ticketDetailLoading) {
    return (
      <div style={{ overflowX: "hidden", width: "100%" }}>
        <Header />

        <div className="main-layout">
          <div className="gifts-body">
            <div className="gifts-body-in">
              <Container fluid className='bg-white' style={{ minHeight: '100vh', paddingTop: '40px', paddingBottom: '40px' }}>
                <GiftLoader
                  text="Loading ticket details..."
                  size="large"
                  showText={true}
                />
              </Container>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show main loading screen when loading FAQ and tickets
  if (loading) {
    return (
      <div style={{ overflowX: "hidden", width: "100%" }}>
        <Header />

        <div className="main-layout">
          <div className="gifts-body">
            <div className="gifts-body-in">
              <Container fluid className='bg-white' style={{ minHeight: '100vh', paddingTop: '40px', paddingBottom: '40px' }}>
                <GiftLoader
                  text="Loading help & support..."
                  size="large"
                  showText={true}
                />
              </Container>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Main help support view
  return (
    <div style={{ overflowX: "hidden", width: "100%" }}>
      <Header />

      {/* Main Layout */}
      <div className="main-layout">
        <div className="gifts-body">
          <div className="gifts-body-in">
            <div className="help-support-container">
              <Container fluid className='bg-white py-5'>
                <motion.div
                  className="help-support-header"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <h1>Help & Support</h1>
                </motion.div>

                <Row>
                  <Col lg={12} className="mx-auto">
                    {/* FAQ Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                      <FAQSection
                        faqs={faqs}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                      />
                    </motion.div>

                    {/* Tickets Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="mt-5"
                    >
                      <TicketTable
                        issues={issues}
                        loading={false} // No individual loading since we handle it at page level
                        onTicketClick={handleTicketClick}
                        onSubmitNewTicket={() => setShowSubmitModal(true)}
                      />
                    </motion.div>
                  </Col>
                </Row>

                {/* Submit Issue Modal */}
                <SubmitTicketModal
                  show={showSubmitModal}
                  onHide={() => setShowSubmitModal(false)}
                  onSubmit={handleSubmitIssue}
                />
              </Container>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HelpSupport;